import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STAGES } from "./crmConst";

const EMPTY = { company: "", contact_name: "", email: "", phone: "", address: "", postal_code: "", city: "", partner_id: null, stage: "prospect", comment: "" };
const FIELDS = [["company", "Société / nom du client *"], ["contact_name", "Contact"], ["email", "Email"], ["phone", "Téléphone"], ["address", "Adresse"], ["postal_code", "Code postal"], ["city", "Ville"]];

export const ClientDialog = ({ open, onOpenChange, client, partners, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(client ? { ...EMPTY, ...client } : EMPTY); }, [open, client]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    if (!f.company.trim()) return toast.error("Le nom du client est requis");
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k]]));
      const r = client ? await api.put(`/crm/clients/${client.id}`, body) : await api.post("/crm/clients", body);
      toast.success(client ? "Client mis à jour" : "Client créé");
      onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid="crm-client-dialog">
        <DialogHeader><DialogTitle>{client ? "Modifier le client" : "Nouveau client / prospect"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, l]) => (
            <div key={k} className={k === "company" || k === "address" ? "sm:col-span-2 space-y-1" : "space-y-1"}>
              <Label className="text-xs">{l}</Label>
              <Input value={f[k] || ""} onChange={set(k)} data-testid={`crm-input-${k}`} />
            </div>
          ))}
          <div className="space-y-1">
            <Label className="text-xs">Partenaire d'origine</Label>
            <Select value={f.partner_id || "genis"} onValueChange={(v) => setF({ ...f, partner_id: v === "genis" ? null : v })}>
              <SelectTrigger data-testid="crm-input-partner"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="genis">Genis Energie (direct)</SelectItem>
                {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Étape</Label>
            <Select value={f.stage} onValueChange={set("stage")}>
              <SelectTrigger data-testid="crm-input-stage"><SelectValue /></SelectTrigger>
              <SelectContent>{STAGES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 space-y-1">
            <Label className="text-xs">Commentaire</Label>
            <Textarea rows={2} value={f.comment || ""} onChange={set("comment")} data-testid="crm-input-comment" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button className="bg-genis hover:bg-genis-dark text-white" disabled={busy} onClick={save} data-testid="crm-client-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
